"use client";

import React, { useState, useEffect, useCallback, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import Logo from "../ui/logo";
import { cn } from "@/lib/utils";
import { ShoppingCart } from "lucide-react";

const ITEMS = [
  { label: "WORK", href: "/work" },
  { label: "KUNST", href: "/art" },
  { label: "STORE", href: "https://store.renehuemer.com" },
  { label: "ABOUT", href: "/about" },
  { label: "CONTACT", href: "/contact" },
];

const ANNOUNCEMENT_BANNER_HEIGHT = 36; // px - matches announcement-banner.tsx h-9 (2.25rem)

interface NavbarProps {
  ecommerceEnabled?: boolean;
  announcementId?: string;
}

const Navbar = ({ ecommerceEnabled = false, announcementId }: NavbarProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const [isScrolling, setIsScrolling] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [showAnnouncementOffset, setShowAnnouncementOffset] = useState(false);
  // Blend mode is applied separately AFTER bg has transitioned to transparent
  const [showBlendMode, setShowBlendMode] = useState(false);

  const updateScrollState = useCallback(() => {
    if (typeof window !== "undefined") {
      const scrolled = window.scrollY > 0;
      setIsScrolling(scrolled);
    }
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }, [isMenuOpen]);

  // Delay blend mode: apply only after bg has transitioned to transparent,
  // remove instantly before bg transitions back to white.
  const wantTransparent = !isScrolling && !isMenuOpen;
  useEffect(() => {
    if (wantTransparent) {
      const timer = setTimeout(() => setShowBlendMode(true), 350);
      return () => clearTimeout(timer);
    } else {
      setShowBlendMode(false);
    }
  }, [wantTransparent]);

  useEffect(() => {
    updateScrollState();
    window.addEventListener("scroll", updateScrollState, { passive: true });
    return () => window.removeEventListener("scroll", updateScrollState);
  }, [updateScrollState]);

  // Force solid navbar on route change to prevent mix-blend-difference flash
  const prevPathname = useRef(pathname);
  useEffect(() => {
    if (prevPathname.current !== pathname) {
      setIsScrolling(true);
      prevPathname.current = pathname;
      // Let scroll handler correct it on next frame
      requestAnimationFrame(updateScrollState);
    }
  }, [pathname, updateScrollState]);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    setHasConsent(consent === 'accepted');
  }, []);

  // Check if announcement banner is visible (to offset navbar position)
  useEffect(() => {
    if (!announcementId) {
      setShowAnnouncementOffset(false);
      return;
    }

    const checkAnnouncementVisibility = () => {
      const consent = localStorage.getItem('cookie-consent') === 'accepted';
      const dismissed = localStorage.getItem(`announcement-dismissed-${announcementId}`) === 'true';
      setShowAnnouncementOffset(consent && !dismissed);
    };

    checkAnnouncementVisibility();

    // Listen for storage changes (other tabs)
    window.addEventListener('storage', checkAnnouncementVisibility);
    // Listen for custom event (same tab)
    window.addEventListener('announcement-dismissed', checkAnnouncementVisibility);

    return () => {
      window.removeEventListener('storage', checkAnnouncementVisibility);
      window.removeEventListener('announcement-dismissed', checkAnnouncementVisibility);
    };
  }, [announcementId]);

  // Hide external STORE link when ecommerce is enabled and user has consented
  // (they can buy directly on-site via Snipcart)
  const navItems = ecommerceEnabled && hasConsent
    ? ITEMS.filter(item => item.label !== "STORE")
    : ITEMS;

  return (
    <>
      {/* Skip to main content link for keyboard users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-white focus:text-black focus:rounded-md focus:shadow-lg"
      >
        Skip to main content
      </a>

      <header
        className={cn(
          "fixed left-0 z-50 w-full transition-all duration-300 ease-in-out border-none shadow-none",
          wantTransparent ? "bg-transparent" : "bg-white",
          showBlendMode && "mix-blend-difference"
        )}
        style={{ top: showAnnouncementOffset ? ANNOUNCEMENT_BANNER_HEIGHT : 0 }}
      >
      <div className="bigger-container mx-auto flex items-center h-20 md:h-24">
        {/* Logo: invert when using mix-blend so white SVG appears black on white bg */}
        <Logo
          wrapperClassName={cn(
            "flex justify-start w-36 md:w-52",
            showBlendMode && "invert"
          )}
        />

        <div className="flex flex-1 items-center justify-end gap-6 md:gap-10">
          <nav className="hidden md:flex items-center">
            <ul className="flex items-center gap-8">
              {navItems.map((item) => (
                <li key={item.label}>
                  <Link
                    href={item.href}
                    {...(item.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className={cn(
                      "text-[1rem] font-bold tracking-[0.2em] transition-all hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2",
                      showBlendMode ? "text-white" : "text-[#000]",
                      pathname === item.href && "underline underline-offset-4"
                    )}
                    aria-current={pathname === item.href ? "page" : undefined}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {ecommerceEnabled && hasConsent && (
            <button className="snipcart-checkout relative group flex items-center p-2 transition-transform hover:scale-110">
              <ShoppingCart
                className={cn("size-5", showBlendMode ? "text-white" : "text-[#000]")}
                strokeWidth={1.5}
              />
              <span className={cn(
                "snipcart-items-count absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full text-[9px] font-bold shadow-sm ring-2 ring-white",
                showBlendMode ? "bg-white text-black" : "bg-[#000] text-white"
              )}>
                0
              </span>
            </button>
          )}

          <div className="flex items-center md:hidden relative z-[70]">
            <button
              className="flex size-8"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label={isMenuOpen ? 'Close menu' : 'Open main menu'}
            >
              <span className="sr-only">{isMenuOpen ? 'Close menu' : 'Open main menu'}</span>
              <div className="absolute top-1/2 left-1/2 block w-6 -translate-x-1/2 -translate-y-1/2">
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute block h-0.5 w-full rounded-full transition duration-500 ease-in-out",
                    isMenuOpen ? "rotate-45 bg-[#000]" : "-translate-y-1.5",
                    showBlendMode ? "bg-white" : "bg-[#000]"
                  )}
                ></span>
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute block h-0.5 w-full rounded-full transition duration-500 ease-in-out",
                    isMenuOpen ? "-rotate-45 bg-[#000]" : "translate-y-1.5",
                    showBlendMode ? "bg-white" : "bg-[#000]"
                  )}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        className={cn(
          "fixed inset-0 z-[60] transition-all duration-500 md:hidden",
          isMenuOpen ? "visible opacity-100" : "invisible opacity-0",
        )}
        aria-hidden={!isMenuOpen}
      >
        <div
          className="absolute inset-0 bg-white/90 backdrop-blur-md"
          onClick={() => setIsMenuOpen(false)}
          role="presentation"
          aria-label="Close menu"
        />
        <nav
          className={cn(
            "bg-white relative ms-auto me-0 flex h-full w-[85%] flex-col p-8 pt-24 transition-transform duration-500 ease-out",
            isMenuOpen ? "translate-x-0" : "translate-x-full",
          )}
          aria-label="Mobile navigation"
        >
          {navItems.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              {...(link.href.startsWith("http") ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              className={cn(
                "py-4 text-left transition-all text-gray-900 text-3xl font-light tracking-tight focus-visible:outline-2 focus-visible:outline-offset-2",
                pathname === link.href && "text-primary font-medium",
              )}
              onClick={() => setIsMenuOpen(false)}
              aria-current={pathname === link.href ? "page" : undefined}
            >
              {link.label}
            </Link>
          ))}
        </nav>
      </div>
    </header>
    </>
  );
};

export default Navbar;
